import React, { useEffect, useState } from "react";
import axios from "axios";
import Grid from "../components/Grid";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography, TextField
} from "@mui/material";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ClaimPage() {

    const [data, setData] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [approveAmount, setApproveAmount] = useState("");
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [totalRows, setTotalRows] = useState(0);

    const token = sessionStorage.getItem("token");
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const role = user.role || "";

    const [filters, setFilters] = useState({
        search: "",
        category: "",
        location: "",
        from: "",
        to: ""
    });

    const updateFilter = (newFilters) => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    useEffect(() => {
        fetchCategories();
        fetchLocations();
    }, []);

    useEffect(() => {
        fetchClaims();
    }, [paginationModel, filters]);

    useEffect(() => {

        const refreshHandler = () => {
            fetchClaims();
        };

        window.addEventListener(
            "pettycash-updated",
            refreshHandler
        );

        return () => {
            window.removeEventListener(
                "pettycash-updated",
                refreshHandler
            );
        };

    }, [paginationModel, filters]);

    const fetchClaims = async () => {
        try {
            const params = new URLSearchParams();

            params.append("transaction_type", "Claim");
            params.append("page", paginationModel.page + 1);
            params.append("limit", paginationModel.pageSize);

            if (filters.search) params.append("search", filters.search);
            if (filters.category) params.append("category", filters.category);
            if (filters.location) params.append("location", filters.location);
            if (filters.from) params.append("startDate", filters.from);
            if (filters.to) params.append("endDate", filters.to);

            const res = await axios.get(
                `${API_BASE_URL}/pettycash/get-transactions?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setData(res.data.data || []);
            setTotalRows(res.data.totalRecords || 0);

        } catch {
            toast.error("Error loading claims");
        }
    };

    const fetchCategories = async()=>{

        try{

            const res = await axios.get(
                `${API_BASE_URL}/cashbook-category/get`,
                {headers:{Authorization:`Bearer ${token}`}}
            );

            const paymentCategories = res.data.data.filter(
                c => c.type === "Payment"
            );
            setCategories(paymentCategories);

        }catch(err){

            toast.error("Error loading categories");

        }

    };

    const fetchLocations = async()=>{

        try{

            const res = await axios.get(
                `${API_BASE_URL}/locations`,
                {headers:{Authorization:`Bearer ${token}`}}
            );

            setLocations(res.data.locations || []);

        }catch(err){

            console.log(err);

        }

    };

    const handleOpenApprove = (row) => {

        let approveValue = row.amount;

        if (
            row.related_reimbursements &&
            row.related_reimbursements.length > 0
        ) {

            approveValue = row.related_reimbursements.reduce(
                (sum, item) =>
                    sum + Number(item.amount || 0),
                0
            );
        }

        setSelectedRow(row);
        setApproveAmount(approveValue);
        setOpenDialog(true);
    };

    const handleApprove = async () => {
        try {

            await axios.put(
                `${API_BASE_URL}/pettycash/approve/${selectedRow._id}`,
                {
                    approve_amount: Number(approveAmount)
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success("Claim approved");

            fetchClaims();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch (err) {
            toast.error(err.response?.data?.message || "Error");
        }

        setOpenDialog(false);
    };

    const handleReject = async (id) => {
        try {
            await axios.put(
                `${API_BASE_URL}/pettycash/reject/${id}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success("Rejected");
            fetchClaims();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch {
            toast.error("Error");
        }
    };

    const issueMoney = async (id) => {
        try {
            await axios.post(
                `${API_BASE_URL}/pettycash/issue/${id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success("Money Issued");
            fetchClaims();

            window.dispatchEvent(
                new Event("pettycash-updated")
            );

        } catch (err) {
            toast.error(err.response?.data?.message || "Error");
        }
    };


    const columns = [
        { field: "transaction_code",
            headerName: "Code",
            flex: 1 ,
            renderCell: (params) => {
                const user = params.row.created_by?.username || "Unknown";
                const location =
                    params.row.cash_book?.location?.location_name ||
                    params.row.location?.location_name ||
                    "Organization";

                return (
                    <div className="flex flex-col justify-center w-full h-full">
                        <span className="font-semibold text-gray-800">
                            {params.value}
                        </span>

                        <div className="flex gap-2 mt-1 text-xs">
                            <span className="bg-gray-100 px-2 py-[2px] rounded">
                                👤 {user}
                            </span>
                            <span className="bg-green-100 text-green-700 px-2 py-[2px] rounded">
                                📍 {location}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            field: "cashbook",
            headerName: "CashBook",
            flex: 1,
            renderCell: p => {
                const book = p.row.cash_book?.cash_book_code

                return(
                    <div className="flex justify-center items-center w-full h-full ">
                        <span> {book}</span>
                    </div>
                )
            }
        },
        {
            field: "amount",
            headerName: "Amount",
            flex: 1 ,
            renderCell: p => {

                const isReimbursementClaim =
                    p.row.related_reimbursements?.length > 0;

                return(

                    <div className="flex justify-center items-center w-full h-full ">

            <span className={
                isReimbursementClaim
                    ? "text-red-600 font-semibold"
                    : "text-green-600 font-semibold"
            }>

                {isReimbursementClaim ? "-" : "+"}
                {" "}Rs {p.row.amount}

            </span>

                    </div>
                )
            }
        },

        {
            field: "linked_reimbursements",
            headerName: "Linked Reimbursements",
            flex: 1.5,
            renderCell: (p) => {

                const items =
                    p.row.related_reimbursements || [];

                if (items.length === 0) {
                    return (
                        <div className="flex justify-center items-center w-full h-full">
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                                Normal Claim
                            </span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center items-center w-full h-full  flex-col gap-1 py-2">

                        {items.map((r, index) => (

                            <span
                                key={index}
                                className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs"
                            >{r.reimbursement_id?.transaction_code}
                            </span>
                        ))}

                    </div>
                );
            }
        },

        { field: "approval_status",
            headerName: "Status",
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: p => {
                const status = p.row.approval_status;
                const payment = p.row.claim_payment_status;

                let label = status;
                let color = "bg-yellow-500";

                if (status === "Approved" && payment === "Pending") {
                    label = "Approved (Not Paid)";
                    color = "bg-blue-500";
                }

                if (
                    status === "Approved" &&
                    p.row.money_received
                ) {

                    label = "Completed";
                    color = "bg-green-700";

                } else if (
                    status === "Approved" &&
                    payment === "MoneyIssued"
                ) {

                    label = "Money Issued";
                    color = "bg-yellow-500";

                } else if (
                    status === "Approved"
                ) {

                    label = "Approved";
                    color = "bg-blue-500";
                }

                if (status === "Rejected") {
                    color = "bg-red-600";
                }

                return (
                    <div className="flex justify-center items-center w-full h-full">
                        <span className={`${color} text-white px-3 py-1 rounded-full text-xs font-medium`}>
                            {label}
                        </span>
                    </div>
                );
            }
        },
        {
            field: "createdAt",
            headerName: "Date",
            flex: 1,
            renderCell: (params) => {
                const date = new Date(params.row.createdAt).toLocaleDateString();

                return(
                    <div className="flex justify-center items-center w-full h-full ">
                        <span> {date}</span>
                    </div>
                )
            }

        },
        ... (role === "Company Admin" || role === "Accountant"
            ? [{
                field: "action",
                headerName: "Action",
                flex: 1,
                renderCell: (p) => {

                    if (
                        p.row.approval_status === "Approved" &&
                        p.row.claim_payment_status === "Pending"
                    ) {
                        return (
                            <div className="flex gap-2 justify-center items-center w-full h-full">
                                <button
                                    onClick={() => issueMoney(p.row._id)}
                                    className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs"
                                >
                                    Cash Issued
                                </button>
                            </div>
                        );
                    }

                    if (role === "Company Admin" &&
                        p.row.approval_status === "Pending") {
                        return (
                            <div className="flex gap-2 justify-center items-center w-full h-full">
                                <button
                                    onClick={() => handleOpenApprove(p.row)}
                                    className="bg-green-600 text-white px-2 py-1 rounded-full text-xs"
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={() => handleReject(p.row._id)}
                                    className="bg-red-600 text-white px-2 py-1 rounded-full text-xs"
                                >
                                    Reject
                                </button>
                            </div>
                        );
                    }

                    return null;
                }
            }] : [])
    ];

    return (
        <div className="p-6">

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-green-700">
                    Claims
                </h1>
            </div>

            <div className="bg-white p-4 rounded shadow">
                <div className="flex flex-wrap gap-2 mb-4">

                    <input
                        placeholder="Search"
                        className="border p-2 flex-1"
                        onChange={e => updateFilter({ search: e.target.value })}
                    />

                    <select
                        onChange={e => updateFilter({ category: e.target.value })}
                        className="border p-2"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => (
                            <option key={c._id} value={c._id}>
                                {c.category_name}
                            </option>
                        ))}
                    </select>

                    <select
                        className="border p-2"
                        onChange={e => updateFilter({ location: e.target.value })}>
                        <option value="">All Locations</option>
                        {locations.map(l => (
                            <option key={l._id} value={l._id}>
                                {l.location_name}
                            </option>
                        ))}
                    </select>

                    <input
                        type="date"
                        className="border p-2"
                        onChange={e => updateFilter({ from: e.target.value })}
                    />

                    <input
                        type="date"
                        className="border p-2"
                        onChange={e => updateFilter({ to: e.target.value })}
                    />

                </div>

                <Grid
                    rows={data}
                    columns={columns}
                    getRowId={(r) => r._id}
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    autoRowHeight={true}
                />
            </div>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>

                <DialogTitle>Approve Claim</DialogTitle>

                <DialogContent>

                    <Typography sx={{ mb: 2 }}>
                        Enter amount
                    </Typography>

                    <TextField
                        fullWidth
                        type="number"
                        value={approveAmount}
                        InputProps={{
                            readOnly: true,
                        }}
                    />

                </DialogContent>

                <DialogActions>

                    <Button onClick={() => setOpenDialog(false)}>
                        Cancel
                    </Button>

                    <Button onClick={handleApprove} variant="contained">
                        Approve
                    </Button>

                </DialogActions>

            </Dialog>

        </div>
    );
}

export default ClaimPage;